import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { track } from "../lib/analytics";
import Reveal from "../components/Reveal";
import PageSEO from "../components/PageSEO";
import VimeoPlayer from "../components/VimeoPlayer";
import { isVimeoUrl } from "../lib/vimeo";

/**
 * /album — teaser landing page for an upcoming release.
 *
 * Structure:
 *  1. Hero — album title, subtitle, optional Vimeo/MP4 background film,
 *     countdown to release_date (if set), and a big email capture card.
 *  2. Tracklist teaser — list of tracks with optional 20s teaser toggle.
 *  3. Follow strip — Spotify / Apple Music / YouTube / Instagram links.
 *
 * All copy is admin-editable via /admin/album. If `enabled` is false the
 * page renders a soft "coming soon" placeholder so the /album route is
 * never a broken 404 while you're setting things up.
 */
const SITE_URL = window.location.origin;

export default function Album() {
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [status, setStatus] = useState(null); // null | "sending" | "success" | "already" | "error"

  useEffect(() => {
    api.get("/album").then(({ data }) => setData(data)).catch(() => setData({}));
    try { track("album_page_viewed", {}); } catch {}
  }, []);

  const countdown = useCountdown(data?.release_date);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email)) {
      setStatus("error");
      return;
    }
    setStatus("sending");
    try {
      const { data: out } = await api.post("/subscribers", {
        email,
        name: name || "",
        source: "album",
      });
      setStatus(out.already_subscribed ? "already" : "success");
      track("album_email_captured", { already_subscribed: !!out.already_subscribed });
      if (!out.already_subscribed) {
        setEmail("");
        setName("");
      }
    } catch {
      setStatus("error");
      track("album_email_capture_failed", {});
    }
  };

  if (!data) {
    return (
      <main className="min-h-[80vh] flex items-center justify-center bg-[#0A0A0C]">
        <p className="overline text-[#8A8A93]">Loading…</p>
      </main>
    );
  }

  if (!data.enabled) {
    return (
      <main data-testid="album-placeholder" className="min-h-[100svh] flex flex-col items-center justify-center bg-[#0A0A0C] px-6 text-center">
        <PageSEO title="Album — Coming soon — Artistry Perspectives" description="An upcoming release from Artistry Perspectives." url={`${SITE_URL}/album`} />
        <p className="overline text-[#C9A050] mb-6">Coming soon</p>
        <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[1.05] tracking-[-0.02em] max-w-3xl">
          Something's <em className="text-[#C9A050]">on its way.</em>
        </h1>
        <p className="mt-6 text-white/60 max-w-md">Check back soon.</p>
      </main>
    );
  }

  const heroIsVimeo = data.hero_video_url ? isVimeoUrl(data.hero_video_url) : false;

  return (
    <main data-testid="album-page" className="bg-[#0A0A0C]">
      <PageSEO
        title={`${data.title || "The Album"} — Artistry Perspectives`}
        description={data.subtitle || data.tagline}
        image={data.cover_image}
        url={`${SITE_URL}/album`}
      />

      {/* HERO */}
      <section data-testid="album-hero" className="relative min-h-[100svh] w-full overflow-hidden">
        {/* Cover image base layer — always visible */}
        {data.cover_image && (
          <img
            src={data.cover_image}
            alt=""
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 0, filter: "brightness(0.45) saturate(0.9)" }}
          />
        )}
        {/* Optional Vimeo/MP4 background film */}
        {data.hero_video_url && heroIsVimeo && (
          <div className="absolute inset-0" style={{ zIndex: 1, opacity: 0.55 }}>
            <VimeoPlayer
              url={data.hero_video_url}
              fallback="-"
              orientation="horizontal"
              premium
              objectFit="cover"
              testid="album-hero"
            />
          </div>
        )}
        {data.hero_video_url && !heroIsVimeo && (
          <video
            src={data.hero_video_url}
            autoPlay
            muted
            loop
            playsInline
            aria-hidden="true"
            className="absolute inset-0 w-full h-full object-cover"
            style={{ zIndex: 1, opacity: 0.55 }}
          />
        )}
        {/* Gradient wash for legibility */}
        <div aria-hidden="true" className="absolute inset-0 pointer-events-none" style={{ zIndex: 2, background: "linear-gradient(180deg, rgba(10,10,12,0.15) 0%, rgba(10,10,12,0.55) 55%, rgba(10,10,12,0.95) 100%)" }} />

        <div className="relative h-full min-h-[100svh] flex flex-col justify-end px-6 md:px-14 lg:px-24 pb-20 md:pb-28" style={{ zIndex: 3 }}>
          {data.tagline && (
            <Reveal>
              <p className="overline text-[#C9A050] mb-8">{data.tagline}</p>
            </Reveal>
          )}
          {data.title && (
            <Reveal delay={0.05}>
              <h1
                data-testid="album-title"
                className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.03em] max-w-4xl"
              >
                {data.title}
              </h1>
            </Reveal>
          )}
          {data.subtitle && (
            <Reveal delay={0.1}>
              <p className="mt-6 text-lg md:text-xl leading-relaxed text-white/70 max-w-2xl whitespace-pre-line">
                {data.subtitle}
              </p>
            </Reveal>
          )}

          {countdown && (
            <Reveal delay={0.2}>
              <div data-testid="album-countdown" className="mt-14 flex flex-wrap gap-6 md:gap-10">
                {[
                  ["Days", countdown.days],
                  ["Hours", countdown.hours],
                  ["Minutes", countdown.minutes],
                  ["Seconds", countdown.seconds],
                ].map(([label, val]) => (
                  <div key={label} className="min-w-[64px]">
                    <div className="font-display text-4xl md:text-6xl tabular-nums leading-none tracking-[-0.02em]">
                      {String(val).padStart(2, "0")}
                    </div>
                    <div className="overline text-[#8A8A93] mt-2 text-xs">{label}</div>
                  </div>
                ))}
              </div>
            </Reveal>
          )}
        </div>
      </section>

      {/* EMAIL CAPTURE */}
      <section data-testid="album-capture" className="px-6 md:px-14 lg:px-24 py-24 md:py-40 border-t border-white/5">
        <div className="grid grid-cols-12 gap-6 md:gap-12">
          <div className="col-span-12 md:col-span-6">
            <Reveal>
              <p className="overline text-[#C9A050] mb-6">The list</p>
              <h2 className="font-display text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-[-0.02em] max-w-md">
                {data.email_capture_heading || "Be the first to hear it."}
              </h2>
              {data.email_capture_note && (
                <p className="mt-6 text-white/60 max-w-md whitespace-pre-line">
                  {data.email_capture_note}
                </p>
              )}
            </Reveal>
          </div>
          <div className="col-span-12 md:col-span-6">
            <Reveal delay={0.1}>
              <form onSubmit={handleSubmit} data-testid="album-form" className="max-w-md space-y-4">
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name (optional)"
                  data-testid="album-input-name"
                  className="w-full bg-transparent border border-white/15 focus:border-[#C9A050] outline-none px-4 py-3 text-white placeholder:text-white/30"
                />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  data-testid="album-input-email"
                  className="w-full bg-transparent border border-white/15 focus:border-[#C9A050] outline-none px-4 py-3 text-white placeholder:text-white/30"
                />
                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="album-submit"
                  className="w-full overline border border-[#C9A050] text-[#C9A050] hover:bg-[#C9A050] hover:text-black px-6 py-4 transition-all disabled:opacity-50"
                >
                  {status === "sending" ? "Sending…" : "Keep me posted →"}
                </button>
                {status === "success" && (
                  <p data-testid="album-success" className="text-sm text-[#C9A050]">You're in. We'll let you know the moment it drops.</p>
                )}
                {status === "already" && (
                  <p data-testid="album-already" className="text-sm text-white/60">You're already on the list — thanks for the enthusiasm.</p>
                )}
                {status === "error" && (
                  <p data-testid="album-error" className="text-sm text-red-400">Please enter a valid email and try again.</p>
                )}
              </form>
            </Reveal>
          </div>
        </div>
      </section>

      {/* TRACKLIST */}
      {(data.tracks && data.tracks.length > 0) && (
        <section data-testid="album-tracks" className="px-6 md:px-14 lg:px-24 py-24 md:py-32 border-t border-white/5">
          <div className="grid grid-cols-12 gap-6 md:gap-10">
            <div className="col-span-12 md:col-span-4">
              <Reveal>
                <p className="overline text-[#C9A050]">The tracklist</p>
                <h2 className="font-display text-3xl md:text-4xl lg:text-5xl leading-[1.05] tracking-[-0.02em] mt-6 max-w-sm">
                  Every song, in order.
                </h2>
              </Reveal>
            </div>
            <div className="col-span-12 md:col-span-8">
              <ul className="divide-y divide-white/10 border-t border-white/10">
                {data.tracks.map((t, i) => (
                  <li key={i} data-testid={`album-track-${i}`} className="py-6 md:py-7 flex items-center gap-6">
                    <span className="overline text-[#8A8A93] w-8 tabular-nums">{String(i + 1).padStart(2, "0")}</span>
                    <div className="flex-1">
                      <div className="font-display text-xl md:text-2xl leading-tight tracking-tight">{t.title || `Track ${i + 1}`}</div>
                      {t.length && <div className="text-xs text-white/40 mt-1">{t.length}</div>}
                    </div>
                    {t.teaser_url && (
                      <a
                        href={t.teaser_url}
                        target="_blank"
                        rel="noreferrer"
                        onClick={() => track("album_track_teaser_clicked", { index: i, title: t.title })}
                        data-testid={`album-track-teaser-${i}`}
                        className="overline text-[#C9A050] hover:text-white transition-colors"
                      >
                        Listen →
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* FOLLOW STRIP */}
      {(data.spotify_url || data.apple_music_url || data.youtube_url || data.instagram_url) && (
        <section data-testid="album-follow" className="px-6 md:px-14 lg:px-24 py-16 md:py-24 border-t border-white/5">
          <div className="flex flex-wrap gap-4 md:gap-6">
            {data.spotify_url && (
              <FollowLink label="Spotify" href={data.spotify_url} onClick={() => track("album_follow_clicked", { platform: "spotify" })} testid="album-follow-spotify" />
            )}
            {data.apple_music_url && (
              <FollowLink label="Apple Music" href={data.apple_music_url} onClick={() => track("album_follow_clicked", { platform: "apple" })} testid="album-follow-apple" />
            )}
            {data.youtube_url && (
              <FollowLink label="YouTube" href={data.youtube_url} onClick={() => track("album_follow_clicked", { platform: "youtube" })} testid="album-follow-youtube" />
            )}
            {data.instagram_url && (
              <FollowLink label="Instagram" href={data.instagram_url} onClick={() => track("album_follow_clicked", { platform: "instagram" })} testid="album-follow-instagram" />
            )}
          </div>
        </section>
      )}
    </main>
  );
}

function FollowLink({ label, href, onClick, testid }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      onClick={onClick}
      data-testid={testid}
      className="overline border border-white/15 hover:border-[#C9A050] hover:text-[#C9A050] px-6 py-3 transition-colors"
    >
      {label} →
    </a>
  );
}

/** Ticks every second while a valid future release_date is set. */
function useCountdown(releaseDate) {
  const target = useMemo(() => {
    if (!releaseDate) return null;
    const t = Date.parse(releaseDate);
    if (Number.isNaN(t)) return null;
    return t;
  }, [releaseDate]);
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  let diff = target - now;
  if (diff <= 0) return null;
  const days = Math.floor(diff / 86_400_000);
  diff -= days * 86_400_000;
  const hours = Math.floor(diff / 3_600_000);
  diff -= hours * 3_600_000;
  const minutes = Math.floor(diff / 60_000);
  diff -= minutes * 60_000;
  const seconds = Math.floor(diff / 1000);
  return { days, hours, minutes, seconds };
}
